import { useCallback, useState, type ReactNode } from 'react'
import {
  useForm,
  type SubmitHandler,
  type UseFormRegister,
} from 'react-hook-form'
import { Modal } from './Modal'
import { MoistureEquilibriumForm } from './MoistureEquilibriumForm'
import {
  MoistureEquilibriumProvider,
  useMoistureEquilibriumContext,
} from '../context/moistureEquilibrium'
import {
  transformData,
  ViabilityConstantsForm,
  type ViabilityConstantsFormData,
} from './ViabilityConstantsForm'

type FormData<T> = T & ViabilityConstantsFormData

type ViabilityFormProps<T extends Record<string, string>> = {
  calculation: (data: {
    [K in keyof FormData<T>]: number
  }) => number
  title: string
  renderResultComponent: (result: number) => ReactNode
  renderExtraFields?: ({
    register,
  }: {
    register: UseFormRegister<T>
  }) => ReactNode
  info: JSX.Element | string
}

function BaseViabilityForm<T extends Record<string, string>>({
  calculation,
  title,
  renderResultComponent,
  renderExtraFields,
  info,
}: ViabilityFormProps<T>) {
  const { register, handleSubmit } = useForm<T>()
  const [result, setresult] = useState<number>()
  const [viabilityConstantsData, setViabilityConstantsData] =
    useState<ViabilityConstantsFormData>()

  const { setCalculatingMoistureEquilibrium, calculatingMoistureEquilibrium } =
    useMoistureEquilibriumContext()

  const onSubmit: SubmitHandler<T> = useCallback(
    (data) => {
      if (
        !viabilityConstantsData ||
        !Object.values(viabilityConstantsData).some((val) => val)
      )
        return

      const newData: FormData<T> = {
        ...data,
        ...viabilityConstantsData,
      }

      const transformed = transformData(newData)

      setresult(calculation(transformed))
    },
    [calculation, viabilityConstantsData],
  )

  return (
    <>
      <Modal
        title="Calculate Equilibrium Moisture Content %"
        open={calculatingMoistureEquilibrium}
        setOpen={setCalculatingMoistureEquilibrium}
      >
        <MoistureEquilibriumForm />
      </Modal>

      <div className="mt-6">
        <h4 className="mb-2 text-xl font-bold text-gray-800">{title}</h4>
        <div className="flex flex-row">
          <div className="sm:w-full md:w-1/2 xl:w-1/3 ">
            <form
              className="w-full rounded-md border border-gray-300 p-4"
              onSubmit={handleSubmit(onSubmit)}
            >
              <ViabilityConstantsForm setValues={setViabilityConstantsData} />

              {renderExtraFields && renderExtraFields({ register })}
              <div className="flex w-full justify-end">
                <button
                  className="rounded border-2 border-serblue-light bg-white py-2 px-2"
                  type="submit"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
          <div className="pl-4 sm:w-full md:w-1/2 xl:w-1/3 ">
            {result && (
              <div className="mb-4 w-full rounded-md border border-gray-300 p-4">
                <div>Result</div>
                {renderResultComponent(result)}
              </div>
            )}
            <div className="w-full rounded-md border border-gray-300 p-4 text-sm">
              <span className="font-bold">About this tool</span>
              <p className="mb-2">{info}</p>
              <p className="mb-2">
                This tool can only be used for species where the parameters of
                the viability equation have been solved (see list in the 'Select
                a preset' tab) and for seeds stored hermetically (i.e. in an
                air-tight pouch or other container).
              </p>
              <div>
                <span className="font-bold">Reference</span>
                <ul>
                  <li className="mb-1">
                    Ellis, R.H. and Roberts, E.H. (1980). Improved equations for
                    the prediction of seed longevity. Annals of Botany,{' '}
                    <span className="font-bold">45</span>, 13-30.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export function ViabilityForm<T extends Record<string, string>>({
  children,
  ...props
}: {
  children?: ReactNode
} & ViabilityFormProps<T>) {
  return (
    <MoistureEquilibriumProvider>
      <BaseViabilityForm<T> {...props}>{children}</BaseViabilityForm>
    </MoistureEquilibriumProvider>
  )
}
